import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import PublicLayout from "@/components/PublicLayout";
import { useLocale, pickLocalized } from "@/contexts/LocaleContext";
import { useSettings } from "@/contexts/SettingsContext";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { MapPin, Phone, Mail, Clock, Loader2, Send, Check } from "lucide-react";

export default function ContactPage() {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const { settings } = useSettings();
  const daysShort = t("days_short", { returnObjects: true }) || [];

  const [page, setPage] = useState(null);
  useEffect(() => {
    api.get("/public/pages/contact").then((r) => setPage(r.data)).catch(() => {});
  }, []);
  const data = page?.sections?.[locale] || page?.sections?.hr || {};

  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/public/contact", { ...form, locale });
      setDone(true);
      toast.success(t("common.thank_you"));
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const coord = settings?.coordinates;
  const bbox = coord ? `${coord.lng - 0.01},${coord.lat - 0.005},${coord.lng + 0.01},${coord.lat + 0.005}` : null;
  const mapUrl = bbox
    ? `https://www.openstreetmap.org/export/embed.html?bbox=${bbox}&layer=mapnik&marker=${coord.lat},${coord.lng}`
    : null;

  return (
    <PublicLayout pageTitle={data.title || t("contact_page.title")} testid="contact-page">
      <section className="max-w-6xl mx-auto px-5 sm:px-8 py-16">
        <p className="text-xs uppercase tracking-[0.3em] text-primary/80">— {t("nav.contact")}</p>
        <h1 className="font-display text-5xl sm:text-6xl mt-3" data-testid="contact-title">
          {data.title || t("contact_page.title")}
        </h1>
        <div
          className="prose prose-invert max-w-2xl mt-5 text-muted-foreground"
          dangerouslySetInnerHTML={{ __html: data.body_html || "" }}
        />

        <div className="grid lg:grid-cols-5 gap-10 mt-14">
          <div className="lg:col-span-2 space-y-5" data-testid="contact-info">
            <div className="border border-border rounded-xl p-5 bg-card space-y-4">
              <div>
                <div className="text-xs uppercase tracking-widest text-primary mb-1 flex items-center gap-1.5">
                  <MapPin className="w-3 h-3" /> {t("contact_page.address_label")}
                </div>
                <p className="text-sm leading-relaxed">{pickLocalized(settings?.address, locale, "")}</p>
              </div>
              {settings?.phone && (
                <div>
                  <div className="text-xs uppercase tracking-widest text-primary mb-1 flex items-center gap-1.5">
                    <Phone className="w-3 h-3" /> {t("contact_page.phone_label")}
                  </div>
                  <a
                    href={`tel:${settings.phone.replace(/\s+/g, "")}`}
                    className="text-sm text-muted-foreground hover:text-primary"
                    data-testid="contact-phone"
                  >
                    {settings.phone}
                  </a>
                </div>
              )}
              {settings?.email && (
                <div>
                  <div className="text-xs uppercase tracking-widest text-primary mb-1 flex items-center gap-1.5">
                    <Mail className="w-3 h-3" /> {t("contact_page.email_label")}
                  </div>
                  <a
                    href={`mailto:${settings.email}`}
                    className="text-sm text-muted-foreground hover:text-primary"
                  >
                    {settings.email}
                  </a>
                </div>
              )}
              <div>
                <div className="text-xs uppercase tracking-widest text-primary mb-2 flex items-center gap-1.5">
                  <Clock className="w-3 h-3" /> {t("contact_page.hours_label")}
                </div>
                <ul className="text-sm text-muted-foreground space-y-0.5">
                  {(settings?.hours || []).map((h, idx) => (
                    <li key={idx} className="flex justify-between">
                      <span>{daysShort[h.day] || ""}</span>
                      <span>{h.closed ? "—" : `${h.open}–${h.close}`}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            {mapUrl && (
              <div className="border border-border rounded-xl overflow-hidden bg-card">
                <div className="text-xs uppercase tracking-widest text-primary px-4 pt-4 pb-2">
                  {t("contact_page.find_us")}
                </div>
                <iframe
                  title="map"
                  src={mapUrl}
                  className="w-full h-56 border-0"
                  loading="lazy"
                  data-testid="contact-map"
                />
              </div>
            )}
          </div>

          <div className="lg:col-span-3">
            {done ? (
              <div
                className="border border-primary/40 bg-primary/5 rounded-2xl p-10 text-center"
                data-testid="contact-form-done"
              >
                <Check className="w-12 h-12 text-primary mx-auto mb-4" />
                <h3 className="font-display text-2xl">{t("common.thank_you")}</h3>
                <Button
                  onClick={() => setDone(false)}
                  variant="outline"
                  className="mt-6 border-border"
                  data-testid="contact-form-reset"
                >
                  {t("contact_page.form_submit")} →
                </Button>
              </div>
            ) : (
              <form
                onSubmit={submit}
                className="border border-border rounded-2xl bg-card p-7 space-y-5"
                data-testid="contact-form"
              >
                <p className="text-sm text-muted-foreground">{data.form_intro}</p>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="cn">{t("contact_page.form_name")}</Label>
                    <Input
                      id="cn"
                      required
                      value={form.name}
                      onChange={update("name")}
                      data-testid="contact-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="ce">{t("contact_page.form_email")}</Label>
                    <Input
                      id="ce"
                      type="email"
                      required
                      value={form.email}
                      onChange={update("email")}
                      data-testid="contact-email-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="cp">{t("contact_page.form_phone")}</Label>
                    <Input
                      id="cp"
                      value={form.phone}
                      onChange={update("phone")}
                      data-testid="contact-phone-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="cs">{t("contact_page.form_subject")}</Label>
                    <Input
                      id="cs"
                      required
                      value={form.subject}
                      onChange={update("subject")}
                      data-testid="contact-subject-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="cm">{t("contact_page.form_message")}</Label>
                  <Textarea
                    id="cm"
                    required
                    rows={6}
                    value={form.message}
                    onChange={update("message")}
                    data-testid="contact-message-input"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={submitting}
                  className="bg-primary text-primary-foreground hover:bg-primary/90"
                  data-testid="contact-submit"
                >
                  {submitting ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" /> {t("contact_page.form_submit")}
                    </>
                  )}
                </Button>
              </form>
            )}
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
